import React from 'react';
import {styled} from '@mui/material/styles';
import MuiTypography from '@mui/material/Typography';
import {compose, spacing, palette, breakpoints, typography} from '@mui/system';
import {makeLineClampJss} from '@practicegenius/pg-util/src/DomUtils';
import {makeSxStyles} from '../base/Material/utils/sx-helpers';

const useResponsiveTypographyStyles = ({
  lineClamp,
  preserveWhitespace,
}: {
  lineClamp?: number;
  preserveWhitespace: boolean;
}) =>
  makeSxStyles(theme => ({
    pageTitle: {
      fontSize: '26px',
      fontWeight: 600,
      lineHeight: '24px',
      color: theme.palette.text.primary,
      [theme.breakpoints.up('md')]: {
        fontSize: '37px',
        fontWeight: 400,
        lineHeight: '36px',
        letterSpacing: '0.25px',
      },
    },
    cardTitle: {
      fontSize: '22px',
      fontWeight: 400,
      lineHeight: '24px',
      letterSpacing: '0.15px',
      color: theme.palette.text.primary,
      [theme.breakpoints.up('md')]: {
        fontSize: '26px',
      },
    },
    sectionTitle: {
      fontSize: '17px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0.15px',
      color: theme.palette.text.primary,
      [theme.breakpoints.up('md')]: {
        fontSize: '22px',
      },
    },
    subsectionTitle: {
      fontSize: '15px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0.1px',
      color: theme.palette.text.primary,
      [theme.breakpoints.up('md')]: {
        fontSize: '17px',
        letterSpacing: '0.15px',
      },
    },
    subtitle: {
      fontSize: '15px',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0.1px',
      color: theme.palette.text.secondary,
      [theme.breakpoints.up('md')]: {
        fontSize: '17px',
        fontWeight: 400,
        letterSpacing: '0.15px',
      },
    },
    mainBody: {
      fontSize: '17px',
      fontWeight: 400,
      color: theme.palette.text.primary,
      lineHeight: '24px',
      letterSpacing: '0.15px',
    },
    secondaryBody: {
      fontSize: '15px',
      fontWeight: 400,
      lineHeight: '20px',
      letterSpacing: '0.25px',
      color: theme.palette.text.secondary,
    },
    lineClamp: (() => {
      if (!lineClamp) return {};
      return {
        ...makeLineClampJss(lineClamp),
        overflow: 'hidden',
      };
    })(),
    preserveWhitespace: (() =>
      preserveWhitespace ? {whiteSpace: 'break-spaces'} : {})(),
  }));

const componentMap: Record<CustomVariant, React.ElementType> = {
  pageTitle: 'h4',
  cardTitle: 'h5',
  sectionTitle: 'h6',
  subsectionTitle: 'h6',
  subtitle: 'p',
  mainBody: 'p',
  secondaryBody: 'p',
};

type MuiTypographyProps = React.ComponentProps<typeof MuiTypography>;
type AllowedMuiTypographyProps = Omit<
  MuiTypographyProps,
  'classes' | 'variant' | 'color'
>;

export type CustomVariant =
  | 'pageTitle'
  | 'cardTitle'
  | 'sectionTitle'
  | 'subsectionTitle'
  | 'subtitle'
  | 'mainBody'
  | 'secondaryBody';

type TypographyProps = AllowedMuiTypographyProps & {
  variant: CustomVariant;
  lineClamp?: number;
  preserveWhitespace?: boolean;
};

const CustomTypography: React.FC<TypographyProps> = React.forwardRef(
  (
    {variant, lineClamp, preserveWhitespace = false, className, ...props},
    ref,
  ) => {
    const [styles, sn] = useResponsiveTypographyStyles({
      lineClamp,
      preserveWhitespace,
    })();
    const isCustom = Boolean(styles[variant]);

    return (
      <MuiTypography
        ref={ref}
        className={className}
        sx={
          isCustom
            ? sn(`${variant}`, 'lineClamp', 'preserveWhitespace')
            : undefined
        }
        variant="inherit"
        // @ts-ignore It doesn't think component is a prop, but it is
        component={isCustom ? componentMap[variant] : undefined}
        {...props}
      />
    );
  },
);

/**
 * Custom responsive Typography variants for my-dot. Accepts Typography props, plus
 * MUI system props for `palette`, `spacing`, and `breakpoints`. If included,
 * MUI system props will override default styling for Typography variants.
 *
 * Ex: <Typography variant="pageTitle" align="right" color="primary.secondary" py={2} lg={{pb: 4}} />
 *
 * As of 1/31/2024, this is now deprecated. This component is a lot less useful since MUI v5 blessed us
 *   with the sx prop. The original idea was to have the product team give us a broader typography system
 *   for responsiveness that would be global to everything in my-dot, but in practice they have not shown
 *   a broader application of that system in newer designs, so everyone appears to have forgotten about it.
 * @deprecated See above, use MUI Typography
 */
export const Typography = styled(CustomTypography)(
  breakpoints(compose(spacing, palette, typography)),
);
